/**
 * 外呼记录Tab组件
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Empty,
  message,
  Tooltip,
  Typography,
} from 'antd';
import {
  PlayCircleOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { queryCallRecords, getRecording, syncCDRs } from '../../services/outbound';
import { OutboundCallRecord, CallStatus } from '../../types/outbound';
import { formatDateTime, formatDuration } from '../../utils/dateFormat';

const { Text } = Typography;

interface CallRecordsTabProps {
  leadId: number;
  refreshTrigger?: number;
}

const CallRecordsTab: React.FC<CallRecordsTabProps> = ({
  leadId,
  refreshTrigger = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<OutboundCallRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadRecords();
  }, [leadId, page, pageSize, refreshTrigger]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const response = await queryCallRecords({
        lead_id: leadId,
        page,
        page_size: pageSize,
      });
      setRecords(response.items);
      setTotal(response.total);
    } catch (error: any) {
      message.error(error.message || '加载外呼记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 同步话单（只同步当前线索的未完成记录）
  const handleSyncCDRs = async () => {
    try {
      setLoading(true);
      const result = await syncCDRs({ lead_id: leadId });
      
      if (result.total === 0) {
        message.info('没有需要同步的记录');
      } else {
        message.success(result.message || `正在同步${result.total}条话单，请稍后刷新查看`);
        // 延迟刷新，等待后台处理
        setTimeout(() => {
          loadRecords();
        }, 3000);
      }
    } catch (error: any) {
      // 提供更友好的错误提示
      const errorMsg = error.message || error.detail || '同步话单失败';
      
      if (errorMsg.includes('频率') || errorMsg.includes('频次')) {
        message.warning('调用过于频繁，请稍后再试（建议等待1分钟）', 5);
      } else if (errorMsg.includes('未完成')) {
        message.info('当前线索没有未完成的外呼记录');
      } else {
        message.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // 播放录音
  const handlePlayRecording = async (record: OutboundCallRecord) => {
    try {
      const recording = await getRecording(record.id);
      if (recording.cos_url) {
        window.open(recording.cos_url, '_blank');
      } else if (recording.original_url) {
        window.open(recording.original_url, '_blank');
      } else {
        message.warning('录音文件不可用');
      }
    } catch (error: any) {
      message.error(error.message || '获取录音失败');
    }
  };

  // 渲染状态标签
  const renderStatusTag = (status: CallStatus) => {
    const statusMap: Record<CallStatus, { text: string; color: string }> = {
      [CallStatus.INITIATED]: { text: '发起中', color: 'processing' },
      [CallStatus.CALLER_RINGING]: { text: '主叫振铃', color: 'processing' },
      [CallStatus.CALLER_ANSWERED]: { text: '主叫接听', color: 'processing' },
      [CallStatus.CALLEE_RINGING]: { text: '被叫振铃', color: 'processing' },
      [CallStatus.CALLEE_ANSWERED]: { text: '被叫接听', color: 'processing' },
      [CallStatus.CONNECTED]: { text: '通话中', color: 'success' },
      [CallStatus.COMPLETED]: { text: '已完成', color: 'success' },
      [CallStatus.FAILED]: { text: '失败', color: 'error' },
      [CallStatus.CANCELLED]: { text: '已取消', color: 'default' },
    };

    const s = statusMap[status] || { text: status, color: 'default' };
    return <Tag color={s.color}>{s.text}</Tag>;
  };

  // 格式化时长
  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}分${secs}秒`;
  };

  // 表格列定义
  const columns: ColumnsType<OutboundCallRecord> = [
    {
      title: '拨打时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text: string) => formatDateTime(text),
    },
    {
      title: '被叫号码',
      dataIndex: 'callee',
      key: 'callee',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: CallStatus) => renderStatusTag(status),
    },
    {
      title: '通话时长',
      dataIndex: 'bill_duration',
      key: 'bill_duration',
      width: 100,
      render: (duration: number) => (
        <Space size={4}>
          <ClockCircleOutlined />
          <Text>{formatDuration(duration)}</Text>
        </Space>
      ),
    },
    {
      title: '费用',
      dataIndex: 'call_cost',
      key: 'call_cost',
      width: 80,
      align: 'right',
      render: (cost: number) =>
        cost > 0 ? (
          <Space size={4}>
            <DollarOutlined />
            <Text>¥{cost.toFixed(2)}</Text>
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: '录音',
      key: 'recording',
      width: 80,
      align: 'center',
      render: (_, record) =>
        record.has_recording ? (
          <Tooltip title="播放录音">
            <Button
              type="link"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handlePlayRecording(record)}
            />
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary">
          系统每30分钟自动同步话单，如有遗漏可点击按钮手动同步当前线索的未完成记录
        </Text>
        <Tooltip title="同步当前线索的未完成外呼记录">
          <Button
            icon={<ReloadOutlined />}
            onClick={handleSyncCDRs}
            loading={loading}
          >
            同步话单
          </Button>
        </Tooltip>
      </div>
      
      {records.length === 0 && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无外呼记录"
        />
      ) : (
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
          size="small"
        />
      )}
    </div>
  );
};

export default CallRecordsTab;
